import { useCallback, useEffect, useState } from 'react';
import {
  safeGetPersistedValue,
  safePersistValue,
} from '../../../hooks/useLocalStorageState';
import { TemplateSummary } from '../models';

const FAVORITE_TEMPLATES_STORAGE_KEY = 'ai-templates.favorite-template-ids';
const favoriteTemplateSubscribers = new Set<(templateIds: string[]) => void>();

const getFavoriteTemplateIds = () =>
  safeGetPersistedValue<string[]>(FAVORITE_TEMPLATES_STORAGE_KEY) ?? [];

const setFavoriteTemplateIds = (templateIds: string[]) => {
  safePersistValue(FAVORITE_TEMPLATES_STORAGE_KEY, templateIds);
  favoriteTemplateSubscribers.forEach(subscriber => subscriber(templateIds));
};

export const useFavoriteTemplatingTemplates = () => {
  const [favoriteTemplateIds, setFavoriteTemplateIdsState] = useState<string[]>(
    () => getFavoriteTemplateIds(),
  );

  useEffect(() => {
    const subscriber = (templateIds: string[]) => {
      setFavoriteTemplateIdsState(templateIds);
    };
    favoriteTemplateSubscribers.add(subscriber);
    return () => {
      favoriteTemplateSubscribers.delete(subscriber);
    };
  }, []);

  const isFavoriteTemplate = useCallback(
    (template: Pick<TemplateSummary, 'durableId'>) => {
      return favoriteTemplateIds.includes(template.durableId);
    },
    [favoriteTemplateIds],
  );

  const toggleFavoriteTemplate = useCallback(
    (template: Pick<TemplateSummary, 'durableId'>) => {
      const currentTemplateIds = getFavoriteTemplateIds();
      if (currentTemplateIds.includes(template.durableId)) {
        setFavoriteTemplateIds(
          currentTemplateIds.filter(
            templateId => templateId !== template.durableId,
          ),
        );
        return;
      }
      setFavoriteTemplateIds([...currentTemplateIds, template.durableId]);
    },
    [],
  );

  const removeFavoriteTemplate = useCallback(
    (template: Pick<TemplateSummary, 'durableId'>) => {
      const currentTemplateIds = getFavoriteTemplateIds();
      if (!currentTemplateIds.includes(template.durableId)) {
        return;
      }
      setFavoriteTemplateIds(
        currentTemplateIds.filter(
          templateId => templateId !== template.durableId,
        ),
      );
    },
    [],
  );

  const migrateFavoriteTemplateIds = useCallback(
    (templates: Pick<TemplateSummary, 'id' | 'durableId'>[]) => {
      const currentTemplateIds = getFavoriteTemplateIds();
      if (currentTemplateIds.length === 0) {
        return;
      }
      const durableIds = new Set(templates.map(template => template.durableId));
      const durableIdById = new Map(
        templates.map(template => [template.id, template.durableId] as const),
      );
      const nextTemplateIds = currentTemplateIds.map(templateId => {
        if (durableIds.has(templateId)) {
          return templateId;
        }
        return durableIdById.get(templateId) ?? templateId;
      });
      const dedupedTemplateIds = [...new Set(nextTemplateIds)];
      if (
        dedupedTemplateIds.length === currentTemplateIds.length &&
        dedupedTemplateIds.every((templateId, index) => {
          return templateId === currentTemplateIds[index];
        })
      ) {
        return;
      }
      setFavoriteTemplateIds(dedupedTemplateIds);
    },
    [],
  );

  return {
    favoriteTemplateIds,
    isFavoriteTemplate,
    toggleFavoriteTemplate,
    removeFavoriteTemplate,
    migrateFavoriteTemplateIds,
  };
};
